//React
import React, { useEffect, useState } from 'react';
//Mantine
import {
  Flex,
  Loader,
  Paper,
  Text,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { AreaChart, BarChart, LineChart } from '@mantine/charts';
import { DatePickerInput } from '@mantine/dates';

//Icons
import { IconCalendar } from '@tabler/icons-react';
//Utils
//Interfaces
import { Organization } from 'interfaces';
import { useSettingsStore } from 'store/SettingsStore';
import { getColorForSite } from 'utils/badgeColors';

interface DashboardChartProps {
  data: {
    [key: string]: string | number;
  }[];
  isLoading: boolean;
  startDate: string;
  endDate: string;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  organization: Organization;
}

export const DashboardChart: React.FC<DashboardChartProps> = ({
  data,
  isLoading,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
}) => {
  const colorScheme = useComputedColorScheme('dark');
  const isDark = colorScheme === 'dark';
  const theme = useMantineTheme();
  const textColor = isDark ? theme.colors.gray[0] : theme.colors.dark[7];
  const { chartType } = useSettingsStore();
  const chartHeight = 'clamp(280px, 50vh, 520px)';
  const formatCurrency = React.useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format,
    []
  );

  const [value, setValue] = useState<[string | null, string | null]>([
    startDate,
    endDate,
  ]);

  useEffect(() => {
    if (value[0] && value[1]) {
      setStartDate(value[0]);
      setEndDate(value[1]);
    }
  }, [value, setStartDate, setEndDate]);

  const series =
    data.length > 0
      ? Object.keys(data[0])
          .filter((key) => key !== 'date')
          .sort((a, b) => a.localeCompare(b))
          .map((key, index) => ({
            dataKey: key,
            name: key,
            label: key,
            color: getColorForSite(index),
          }))
      : [];

  return (
    <Paper
      withBorder
      p="lg"
      radius="lg"
      mt="md"
      c={textColor}
      style={{
        position: 'relative',
        overflow: 'hidden',
        backdropFilter: 'blur(16px)',
        background: isDark ? '#26282E' : '#EFF0F5',
        borderColor: isDark
          ? 'rgba(255, 255, 255, 0.06)'
          : 'rgba(15, 23, 42, 0.08)',
        boxShadow: '0 12px 30px rgba(15, 23, 42, 0.22)',
        transition:
          'transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
      }}
      data-testid="dashboard-chart"
    >
      <Text size="lg" mb="md">
        Claim Amounts by Site
      </Text>
      {isLoading ? (
        <Flex justify="center" align="center" h={chartHeight} w={'100%'}>
          <Loader size={'xl'} type="bars" />
        </Flex>
      ) : (
        <>
          <DatePickerInput
            type="range"
            label="Select a date range"
            placeholder="Select a date range"
            value={value}
            onChange={setValue}
            leftSection={<IconCalendar />}
          />
          {chartType === 'line' && (
            <LineChart
              data={data}
              series={series}
              dataKey="date"
              h={chartHeight}
              xAxisLabel="Date"
              yAxisLabel="Dollars"
              curveType="monotone"
              tooltipAnimationDuration={200}
              valueFormatter={formatCurrency}
              withTooltip
              withXAxis
              withLegend
              withYAxis
            />
          )}
          {chartType === 'bar' && (
            <BarChart
              data={data}
              series={series}
              dataKey="date"
              h={chartHeight}
              xAxisLabel="Date"
              yAxisLabel="Dollars"
              tooltipAnimationDuration={200}
              valueFormatter={formatCurrency}
              withTooltip
              withXAxis
              withLegend
              withYAxis
              type="stacked"
            />
          )}
          {chartType === 'area' && (
            <AreaChart
              data={data}
              series={series}
              dataKey="date"
              h={chartHeight}
              xAxisLabel="Date"
              yAxisLabel="Dollars"
              curveType="monotone"
              tooltipAnimationDuration={200}
              valueFormatter={formatCurrency}
              withTooltip
              withXAxis
              withLegend
              withYAxis
            />
          )}
        </>
      )}
    </Paper>
  );
};
